import { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import {
  listSubscriptions,
  type SubscriptionItem,
  type SubscriberType,
  type SubscriptionListParams,
} from "../../services/subscriptionService";
import CustomSelect, { type CustomSelectOption } from "../../components/common/CustomSelect";
import Modal from "../../components/common/Modal";
import {
  CreditCard,
  Search,
  ChevronLeft,
  ChevronRight,
  Mail,
  Building2,
  Stethoscope,
  Users,
  Eye,
  Calendar,
  DollarSign,
  Tag,
  Clock,
  CheckCircle2,
  AlertCircle,
  XCircle,
  HelpCircle,
} from "lucide-react";

const STATUS_OPTIONS: CustomSelectOption[] = [
  { label: "All Status", value: "" },
  { label: "Active", value: "Active" },
  { label: "Pending", value: "Pending" },
  { label: "Expired", value: "Expired" },
  { label: "Cancelled", value: "Cancelled" },
];

const TYPE_OPTIONS: CustomSelectOption[] = [
  { label: "All Types", value: "" },
  { label: "Hospital", value: "hospital" },
  { label: "Doctor", value: "doctor" },
  { label: "Public User", value: "public" },
];

const formatCurrency = (amount: number | string, currency: string = "USD"): string => {
  const num = Number(amount) || 0;
  const curr = (currency || "USD").toUpperCase().trim();
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: curr,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  } catch {
    return `${curr} ${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
};

const formatDate = (dateStr?: string | null): string => {
  if (!dateStr) return "—";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "—";
  }
};

const getFiltersFromSearch = (search: string) => {
  const params = new URLSearchParams(search);
  const status = params.get("status") || "";
  const type = params.get("subscriberType") || params.get("type") || "";
  return { status, type };
};

const SubscriptionListingPage = () => {
  const location = useLocation();

  const [subscriptions, setSubscriptions] = useState<SubscriptionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Filters
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>(() => getFiltersFromSearch(location.search).status);
  const [typeFilter, setTypeFilter] = useState<string>(() => getFiltersFromSearch(location.search).type);
  const [page, setPage] = useState(1);
  const limit = 10;

  // View Modal
  const [viewItem, setViewItem] = useState<SubscriptionItem | null>(null);

  useEffect(() => {
    const filters = getFiltersFromSearch(location.search);
    if (filters.status) setStatusFilter(filters.status);
    if (filters.type) setTypeFilter(filters.type);
  }, [location.search]);

  const fetchSubscriptions = useCallback(async (isSearch = false) => {
    if (isSearch) setSearching(true);
    else setLoading(true);

    try {
      const params: SubscriptionListParams = { page, limit };
      if (search.trim()) params.search = search.trim();
      if (statusFilter) params.status = statusFilter;
      if (typeFilter) params.subscriberType = typeFilter;

      const result = await listSubscriptions(params);
      setSubscriptions(result.subscriptions || []);
      setTotal(result.total || 0);
      setTotalPages(result.totalPages || 1);
    } catch {
      toast.error("Failed to load subscriptions");
      setSubscriptions([]);
    } finally {
      setLoading(false);
      setSearching(false);
    }
  }, [page, search, statusFilter, typeFilter]);

  useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  return (
    <div className="space-y-6">
      {/* Filters Bar */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          {/* Search bar */}
          <div className="relative flex-1">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search by subscriber name, email, or subscription ID..."
              className="w-full h-10 pl-10 pr-4 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
            />
          </div>

          {/* Status Dropdown */}
          <div className="w-full sm:w-44 shrink-0">
            <CustomSelect
              value={statusFilter}
              onChange={(val) => {
                setStatusFilter(val);
                setPage(1);
              }}
              options={STATUS_OPTIONS}
              variant="filter"
              placeholder="All Status"
              className="h-10"
            />
          </div>

          {/* Subscriber Type Dropdown (Must appear immediately after Status dropdown) */}
          <div className="w-full sm:w-44 shrink-0">
            <CustomSelect
              value={typeFilter}
              onChange={(val) => {
                setTypeFilter(val);
                setPage(1);
              }}
              options={TYPE_OPTIONS}
              variant="filter"
              placeholder="All Types"
              className="h-10"
            />
          </div>
        </div>
      </div>

      {/* Table Container */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden relative">
        {/* Searching bar indicator */}
        {searching && (
          <div className="absolute top-0 left-0 right-0 h-1 bg-primary-100 overflow-hidden z-10">
            <div className="h-full bg-primary-500 animate-loading-bar"></div>
          </div>
        )}

        {loading ? (
          <div className="p-12 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-gray-500 animate-pulse">Loading subscriptions...</p>
          </div>
        ) : subscriptions.length === 0 ? (
          <div className="p-12 text-center">
            <CreditCard size={48} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 text-lg font-medium">No subscriptions found</p>
            <p className="text-gray-400 text-sm mt-1">
              {search || statusFilter || typeFilter
                ? "Try adjusting your search or filter criteria"
                : "No subscriber records are available in the platform"}
            </p>
          </div>
        ) : (
          <>
            <div className="w-full overflow-x-auto xl:overflow-x-hidden">
              <table className="w-full table-fixed text-left border-collapse">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    {/* 1. Subscriber Name */}
                    <th
                      className="px-1.5 2xl:px-2 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-[28%] sm:w-[28%] lg:w-[21%] xl:w-[22%] 2xl:w-[25%]"
                      title="Subscriber Name"
                    >
                      <span className="hidden sm:inline">Subscriber</span>
                      <span className="sm:hidden">Name</span>
                    </th>

                    {/* 2. Subscriber Type */}
                    <th
                      className="px-1.5 2xl:px-2 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-[20%] sm:w-[20%] lg:w-[12%] xl:w-[10%] 2xl:w-[10%]"
                      title="Subscriber Type"
                    >
                      Type
                    </th>

                    {/* 3. Email: hidden on mobile/tablet < lg */}
                    <th
                      className="hidden lg:table-cell px-1.5 2xl:px-2 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider lg:w-[22%] xl:w-[20%] 2xl:w-[21%]"
                      title="Email Address"
                    >
                      Email
                    </th>

                    {/* 4. Subscription Plan */}
                    <th
                      className="px-1.5 2xl:px-2 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-[24%] sm:w-[24%] lg:w-[15%] xl:w-[13%] 2xl:w-[13%]"
                      title="Subscription Plan"
                    >
                      <span className="hidden xl:inline">Subscription </span>Plan
                    </th>

                    {/* 5. Billing Cycle: hidden on < xl, shown inside Plan cell */}
                    <th
                      className="hidden xl:table-cell px-1.5 2xl:px-2 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider xl:w-[9%] 2xl:w-[9%]"
                      title="Billing Cycle"
                    >
                      Billing Cycle
                    </th>

                    {/* 6. Subscription Amount */}
                    <th
                      className="hidden lg:table-cell px-1.5 2xl:px-2 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider lg:w-[10%] xl:w-[9.5%] 2xl:w-[9%]"
                      title="Subscription Amount with Currency"
                    >
                      Amount
                    </th>

                    {/* 7. Subscription Status */}
                    <th
                      className="px-1.5 2xl:px-2 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-[16%] sm:w-[16%] lg:w-[10%] xl:w-[9%] 2xl:w-[8%]"
                      title="Subscription Status"
                    >
                      Status
                    </th>

                    {/* 8. Actions */}
                    <th
                      className="px-1.5 2xl:px-2 py-3.5 text-center text-xs font-semibold text-gray-500 uppercase tracking-normal 2xl:tracking-wider w-[12%] sm:w-[12%] lg:w-[10%] xl:w-[7.5%] 2xl:w-[5%]"
                      title="Actions"
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {subscriptions.map((sub) => (
                    <tr
                      key={sub.id}
                      className="hover:bg-gray-50/80 transition-colors group cursor-default"
                    >
                      {/* 1. Subscriber Name */}
                      <td className="px-1.5 2xl:px-2 py-3">
                        <div className="flex items-center gap-2 min-w-0">
                          <SubscriberIcon type={sub.subscriberType} />
                          <div className="min-w-0 flex-1">
                            <p
                              className="text-xs sm:text-sm font-semibold text-gray-900 group-hover:text-primary-700 transition-colors truncate"
                              title={sub.subscriberName}
                            >
                              {sub.subscriberName}
                            </p>
                          </div>
                        </div>
                      </td>

                      {/* 2. Subscriber Type */}
                      <td className="px-1.5 2xl:px-2 py-3">
                        <SubscriberTypeBadge type={sub.subscriberType} />
                      </td>

                      {/* 3. Email */}
                      <td className="hidden lg:table-cell px-1.5 2xl:px-2 py-3">
                        <span
                          className="flex items-center gap-1.5 text-xs text-gray-600 truncate"
                          title={sub.email}
                        >
                          <Mail size={12} className="text-gray-400 shrink-0" />
                          <span className="truncate">{sub.email}</span>
                        </span>
                      </td>

                      {/* 4. Subscription Plan */}
                      <td className="px-1.5 2xl:px-2 py-3">
                        <span
                          className="text-xs sm:text-sm font-medium text-gray-900 truncate block"
                          title={sub.planName}
                        >
                          {sub.planName}
                        </span>
                        {/* On screens below xl where Billing Cycle column is hidden, show cycle here */}
                        <span className="text-[10px] sm:text-[11px] text-gray-400 capitalize xl:hidden block truncate">
                          {sub.billingCycle}
                        </span>
                        {/* On screens below lg where Amount column is hidden, show amount here */}
                        <span className="text-[10px] sm:text-[11px] font-semibold text-gray-700 lg:hidden block">
                          {formatCurrency(sub.amount, sub.currency)}
                        </span>
                      </td>

                      {/* 5. Billing Cycle */}
                      <td className="hidden xl:table-cell px-1.5 2xl:px-2 py-3 whitespace-nowrap">
                        <span className="text-xs text-gray-600 capitalize">
                          {sub.billingCycle}
                        </span>
                      </td>

                      {/* 6. Subscription Amount with Currency */}
                      <td className="hidden lg:table-cell px-1.5 2xl:px-2 py-3 whitespace-nowrap">
                        <span className="text-xs sm:text-sm font-semibold text-gray-900">
                          {formatCurrency(sub.amount, sub.currency)}
                        </span>
                      </td>

                      {/* 7. Subscription Status */}
                      <td className="px-1.5 2xl:px-2 py-3 whitespace-nowrap">
                        <SubscriptionStatusBadge status={sub.status} />
                      </td>

                      {/* 8. Actions */}
                      <td className="px-1.5 2xl:px-2 py-3 text-center whitespace-nowrap">
                        <div className="flex items-center justify-center">
                          <button
                            type="button"
                            onClick={() => setViewItem(sub)}
                            className="inline-flex h-7 w-7 items-center justify-center rounded-full text-blue-600 hover:bg-blue-50 hover:text-blue-700 transition-all cursor-pointer p-0"
                            title="View subscription"
                          >
                            <Eye size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-3">
                <p className="text-sm text-gray-500">
                  Showing {(page - 1) * limit + 1}–{Math.min(page * limit, total)} of {total}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <span className="text-sm text-gray-700 font-medium px-2">
                    {page} / {totalPages}
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── View Subscription Modal ────────────────────────────── */}
      {viewItem && (
        <Modal
          isOpen={!!viewItem}
          onClose={() => setViewItem(null)}
          title="Subscription Details"
          subtitle={`Viewing details for ${viewItem.subscriberName}`}
        >
          <div className="p-6 space-y-6">
            {/* Header info card */}
            <div className="flex items-center gap-4 p-4 rounded-xl bg-gray-50 border border-gray-100">
              <div className="h-12 w-12 rounded-xl flex items-center justify-center shrink-0 bg-white shadow-xs border border-gray-200">
                <SubscriberIcon type={viewItem.subscriberType} />
              </div>
              <div className="min-w-0 flex-1">
                <h4 className="text-base font-bold text-gray-900 truncate">
                  {viewItem.subscriberName}
                </h4>
                <p className="text-xs text-gray-500 truncate flex items-center gap-1.5 mt-0.5">
                  <Mail size={12} className="text-gray-400" />
                  {viewItem.email}
                </p>
              </div>
              <SubscriptionStatusBadge status={viewItem.status} />
            </div>

            {/* Subscriber Information */}
            <div>
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Subscriber Information
              </h3>
              <div className="grid grid-cols-2 gap-4 text-sm bg-white p-3 rounded-lg border border-gray-100">
                <ViewField icon={<Users size={14} />} label="Subscriber Type" value={formatSubscriberType(viewItem.subscriberType)} />
                <ViewField icon={<Mail size={14} />} label="Email Address" value={viewItem.email} />
                <ViewField icon={<Tag size={14} />} label="Subscription ID" value={viewItem.id} />
                <ViewField icon={<Tag size={14} />} label="Subscriber ID" value={viewItem.subscriberId || "—"} />
              </div>
            </div>

            {/* Plan & Billing Details */}
            <div>
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Plan & Billing Details
              </h3>
              <div className="grid grid-cols-2 gap-4 text-sm bg-white p-3 rounded-lg border border-gray-100">
                <ViewField icon={<CreditCard size={14} />} label="Subscription Plan" value={viewItem.planName} />
                <ViewField icon={<Clock size={14} />} label="Billing Cycle" value={viewItem.billingCycle} />
                <ViewField
                  icon={<DollarSign size={14} />}
                  label="Subscription Amount"
                  value={formatCurrency(viewItem.amount, viewItem.currency)}
                />
                <ViewField icon={<CheckCircle2 size={14} />} label="Auto Renew" value={viewItem.autoRenew ? "Enabled" : "Disabled"} />
              </div>
            </div>

            {/* Schedule & Lifecycle */}
            <div>
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Schedule & Timeline
              </h3>
              <div className="grid grid-cols-2 gap-4 text-sm bg-white p-3 rounded-lg border border-gray-100">
                <ViewField icon={<Calendar size={14} />} label="Start Date" value={formatDate(viewItem.startDate)} />
                <ViewField icon={<Calendar size={14} />} label="End Date" value={formatDate(viewItem.endDate)} />
                <ViewField icon={<Clock size={14} />} label="Created On" value={formatDate(viewItem.createdAt)} />
                <ViewField icon={<Clock size={14} />} label="Last Updated" value={formatDate(viewItem.updatedAt)} />
              </div>
            </div>

            {/* Notes if any */}
            {viewItem.notes && (
              <div>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  Notes
                </h3>
                <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg border border-gray-100">
                  {viewItem.notes}
                </p>
              </div>
            )}

            <div className="pt-2 flex justify-end">
              <button
                type="button"
                onClick={() => setViewItem(null)}
                className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

// ── Helper Components ──────────────────────────────────────────

const formatSubscriberType = (type: SubscriberType | string) => {
  switch (String(type).toLowerCase()) {
    case "hospital":
      return "Hospital";
    case "doctor":
      return "Doctor";
    case "public":
      return "Public User";
    default:
      return type;
  }
};

const SubscriberIcon = ({ type }: { type: SubscriberType | string }) => {
  switch (String(type).toLowerCase()) {
    case "hospital":
      return (
        <div className="h-8 w-8 rounded-lg bg-teal-50 flex items-center justify-center text-teal-600 shrink-0">
          <Building2 size={16} />
        </div>
      );
    case "doctor":
      return (
        <div className="h-8 w-8 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600 shrink-0">
          <Stethoscope size={16} />
        </div>
      );
    case "public":
    default:
      return (
        <div className="h-8 w-8 rounded-lg bg-purple-50 flex items-center justify-center text-purple-600 shrink-0">
          <Users size={16} />
        </div>
      );
  }
};

const SubscriberTypeBadge = ({ type }: { type: SubscriberType | string }) => {
  switch (String(type).toLowerCase()) {
    case "hospital":
      return (
        <span className="inline-flex items-center gap-1 px-1.5 2xl:px-2 py-0.5 rounded-full text-[11px] 2xl:text-xs font-medium bg-teal-50 text-teal-700 border border-teal-100 truncate">
          <Building2 size={11} className="shrink-0" />
          <span className="truncate">Hospital</span>
        </span>
      );
    case "doctor":
      return (
        <span className="inline-flex items-center gap-1 px-1.5 2xl:px-2 py-0.5 rounded-full text-[11px] 2xl:text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-100 truncate">
          <Stethoscope size={11} className="shrink-0" />
          <span className="truncate">Doctor</span>
        </span>
      );
    case "public":
    default:
      return (
        <span className="inline-flex items-center gap-1 px-1.5 2xl:px-2 py-0.5 rounded-full text-[11px] 2xl:text-xs font-medium bg-purple-50 text-purple-700 border border-purple-100 truncate">
          <Users size={11} className="shrink-0" />
          <span className="truncate">Public User</span>
        </span>
      );
  }
};

const SubscriptionStatusBadge = ({ status }: { status: string }) => {
  let classes = "bg-gray-100 text-gray-600 border border-gray-200";
  let icon = <HelpCircle size={12} />;

  switch (status?.toUpperCase()) {
    case "ACTIVE":
      classes = "bg-green-50 text-green-700 border border-green-200";
      icon = <CheckCircle2 size={12} className="text-green-600" />;
      break;
    case "PENDING":
      classes = "bg-amber-50 text-amber-700 border border-amber-200";
      icon = <AlertCircle size={12} className="text-amber-600" />;
      break;
    case "EXPIRED":
      classes = "bg-gray-100 text-gray-600 border border-gray-200";
      icon = <Clock size={12} className="text-gray-500" />;
      break;
    case "CANCELLED":
      classes = "bg-red-50 text-red-600 border border-red-200";
      icon = <XCircle size={12} className="text-red-500" />;
      break;
  }

  return (
    <span className={`inline-flex items-center gap-1 px-1.5 2xl:px-2 py-0.5 rounded-full text-[11px] 2xl:text-xs font-medium ${classes}`}>
      {icon}
      <span>{status || "Active"}</span>
    </span>
  );
};

const ViewField = ({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value?: string | number | null;
}) => (
  <div>
    <p className="text-gray-500 mb-0.5 text-xs">{label}</p>
    <p className="font-medium text-gray-900 flex items-center gap-1.5">
      <span className="text-gray-400">{icon}</span>
      <span className="truncate">{value != null && value !== "" ? String(value) : "—"}</span>
    </p>
  </div>
);

export default SubscriptionListingPage;
